(function () {
  if (customElements.get("croma-hero-slideshow")) {
    return;
  }

  class CromaHeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.slides = Array.from(this.querySelectorAll("[data-hero-slideshow-slide]"));
      this.dots = Array.from(this.querySelectorAll("[data-hero-slideshow-dot]"));
      this.previousButtons = Array.from(this.querySelectorAll("[data-hero-slideshow-prev]"));
      this.nextButtons = Array.from(this.querySelectorAll("[data-hero-slideshow-next]"));
      this.videos = this.slides.map((slide) => slide.querySelector(".croma-hero-slideshow__video"));
      this.pendingVideoPlays = new WeakSet();
      this.editorSelected = false;
      this.activeIndex = this.slides.findIndex((slide) => slide.classList.contains("is-active"));
      this.timer = null;
      this.exitTimer = null;
      this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.onDotClick = (event) => this.goTo(Number(event.currentTarget.dataset.heroSlideshowIndex), true);
      this.onPreviousClick = () => this.goTo(this.activeIndex - 1, true);
      this.onNextClick = () => this.goTo(this.activeIndex + 1, true);
      this.onMouseEnter = () => this.stopAutoplay();
      this.onMouseLeave = () => this.updateAutoplay();
      this.onFocusIn = () => { this.stopAutoplay(); this.pauseVideos(); };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.updateAutoplay();
        }
      };
      this.onVisibilityChange = () => this.updateAutoplay();
      this.onReducedMotionChange = () => this.updateAutoplay();
      this.onBlockSelect = (event) => {
        const index = this.slides.indexOf(event.target.closest("[data-hero-slideshow-slide]"));
        if (index < 0) return;
        this.editorSelected = true;
        this.goTo(index);
        this.stopAutoplay();
      };
      this.onBlockDeselect = () => {
        this.editorSelected = false;
        this.updateAutoplay();
      };
    }

    connectedCallback() {
      if (this.activeIndex < 0) {
        this.activeIndex = 0;
      }

      this.dots.forEach((dot) => {
        dot.addEventListener("click", this.onDotClick);
      });
      this.previousButtons.forEach((button) => {
        button.addEventListener("click", this.onPreviousClick);
      });
      this.nextButtons.forEach((button) => {
        button.addEventListener("click", this.onNextClick);
      });

      this.addEventListener("mouseenter", this.onMouseEnter);
      this.addEventListener("mouseleave", this.onMouseLeave);
      this.addEventListener("focusin", this.onFocusIn);
      this.addEventListener("focusout", this.onFocusOut);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.reducedMotionQuery.addEventListener("change", this.onReducedMotionChange);
      this.addEventListener("shopify:block:select", this.onBlockSelect);
      this.addEventListener("shopify:block:deselect", this.onBlockDeselect);
      this.goTo(this.activeIndex);
      this.updateAutoplay();
    }

    disconnectedCallback() {
      this.dots.forEach((dot) => {
        dot.removeEventListener("click", this.onDotClick);
      });
      this.previousButtons.forEach((button) => {
        button.removeEventListener("click", this.onPreviousClick);
      });
      this.nextButtons.forEach((button) => {
        button.removeEventListener("click", this.onNextClick);
      });

      this.removeEventListener("mouseenter", this.onMouseEnter);
      this.removeEventListener("mouseleave", this.onMouseLeave);
      this.removeEventListener("focusin", this.onFocusIn);
      this.removeEventListener("focusout", this.onFocusOut);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.reducedMotionQuery.removeEventListener("change", this.onReducedMotionChange);
      this.removeEventListener("shopify:block:select", this.onBlockSelect);
      this.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
      this.stopAutoplay();
      this.pauseVideos();
      this.clearExitingSlides();
    }

    goTo(index, restartAutoplay = false) {
      if (!this.slides.length) {
        return;
      }

      const nextIndex = (index + this.slides.length) % this.slides.length;
      const previousIndex = this.activeIndex;
      const previousSlide = this.slides[previousIndex];
      const nextSlide = this.slides[nextIndex];

      this.clearExitingSlides();

      if (previousSlide && previousSlide !== nextSlide) {
        previousSlide.classList.add("is-exiting");
        this.exitTimer = window.setTimeout(() => {
          previousSlide.classList.remove("is-exiting");
          this.exitTimer = null;
        }, this.getTransitionDuration() + 50);
      }

      this.activeIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        slide.inert = !isActive;
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });

      this.updateVideos();

      if (restartAutoplay) {
        this.updateAutoplay();
      }
    }

    updateAutoplay() {
      this.stopAutoplay();
      this.updateVideos();

      if (
        this.slides.length < 2 ||
        this.editorSelected ||
        this.reducedMotionQuery.matches ||
        document.hidden ||
        this.matches(":hover") ||
        this.contains(document.activeElement)
      ) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.goTo(this.activeIndex + 1);
      }, this.getSlideDuration());
    }

    canPlayVideo(index) {
      return this.isConnected && index === this.activeIndex &&
        !this.reducedMotionQuery.matches && !document.hidden &&
        !this.contains(document.activeElement);
    }

    updateVideos() {
      this.videos.forEach((video, index) => {
        if (!video) return;
        if (!this.canPlayVideo(index)) {
          video.pause();
          return;
        }
        if (!video.paused || this.pendingVideoPlays.has(video)) return;

        video.muted = true;
        this.pendingVideoPlays.add(video);
        let interrupted = false;
        // Autoplay can be rejected; keep the poster and the slideshow usable.
        video.play().then(() => {
          if (!this.canPlayVideo(index)) video.pause();
        }).catch((error) => {
          interrupted = error.name === "AbortError";
        }).finally(() => {
          this.pendingVideoPlays.delete(video);
          if (interrupted && this.canPlayVideo(index)) this.updateVideos();
        });
      });
    }

    pauseVideos() {
      this.videos.forEach((video) => video?.pause());
    }

    getSlideDuration() {
      const duration = Number(this.dataset.slideDuration);

      if (!Number.isFinite(duration) || duration < 1000) {
        return 5000;
      }

      return duration;
    }

    getTransitionDuration() {
      const styles = window.getComputedStyle(this);
      const duration = styles.getPropertyValue("--croma-hero-slideshow-transition-duration").trim();

      if (duration.endsWith("ms")) {
        return Number.parseFloat(duration);
      }

      if (duration.endsWith("s")) {
        return Number.parseFloat(duration) * 1000;
      }

      return 700;
    }

    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    }

    clearExitingSlides() {
      if (this.exitTimer) {
        window.clearTimeout(this.exitTimer);
        this.exitTimer = null;
      }

      this.slides.forEach((slide) => {
        slide.classList.remove("is-exiting");
      });
    }
  }

  customElements.define("croma-hero-slideshow", CromaHeroSlideshow);
})();
